import React, { useState } from 'react';
import {
    X,
    AlertTriangle,
    Pause,
    Play,
    DollarSign,
    TrendingUp,
    Loader2
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

const ActionConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    action,
    entity,
    metrics,
    impactAmount,
    impactType,
    loading = false
}) => {
    const [budgetAmount, setBudgetAmount] = useState(metrics?.currentBudget || 0);

    if (!isOpen) return null;

    const getActionConfig = () => {
        switch (action) {
            case 'pause':
                return {
                    title: 'Pause Campaign',
                    icon: Pause,
                    iconColor: 'var(--color-warning)',
                    description: `Are you sure you want to pause "${entity?.name}"?`,
                    warning: 'This will stop all ads in this campaign from running.',
                    confirmText: 'Pause Campaign',
                    confirmColor: 'var(--color-warning)'
                };
            case 'resume':
                return {
                    title: 'Resume Campaign',
                    icon: Play,
                    iconColor: 'var(--color-success)',
                    description: `Resume "${entity?.name}"?`,
                    warning: 'This will restart ads in this campaign.',
                    confirmText: 'Resume Campaign',
                    confirmColor: 'var(--color-success)'
                };
            case 'scale':
                return {
                    title: 'Scale Budget',
                    icon: TrendingUp,
                    iconColor: 'var(--color-primary)',
                    description: `Increase budget for "${entity?.name}"?`,
                    warning: 'Higher budgets may increase reach but also spend.',
                    confirmText: 'Update Budget',
                    confirmColor: 'var(--color-primary)',
                    showBudgetInput: true
                };
            default:
                return {
                    title: 'Confirm Action',
                    icon: AlertTriangle,
                    iconColor: 'var(--color-text-secondary)',
                    description: `Proceed with action on "${entity?.name}"?`,
                    confirmText: 'Confirm',
                    confirmColor: 'var(--color-primary)'
                };
        }
    };

    const config = getActionConfig();
    const ActionIcon = config.icon;

    const handleConfirm = () => {
        if (config.showBudgetInput) {
            onConfirm({ amount: budgetAmount });
        } else {
            onConfirm();
        }
    };

    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase()) {
            case 'meta': return '📘';
            case 'google': return '🔵';
            case 'tiktok': return '🎵';
            case 'amazon': return '📦';
            default: return '📊';
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: 'var(--space-4)'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-xl)',
                    width: '100%',
                    maxWidth: '480px',
                    boxShadow: 'var(--shadow-xl)',
                    overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-5)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-full)',
                            background: `${config.iconColor}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <ActionIcon size={20} style={{ color: config.iconColor }} />
                        </div>
                        <h2 style={{
                            margin: 0,
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)'
                        }}>
                            {config.title}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: 'var(--space-5)' }}>
                    {/* Entity Info */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        background: 'var(--color-surface)',
                        borderRadius: 'var(--radius-lg)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <span style={{ fontSize: '24px' }}>
                            {getPlatformIcon(entity?.platform)}
                        </span>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontWeight: 600,
                                color: 'var(--color-text-primary)',
                                marginBottom: '2px'
                            }}>
                                {entity?.name || 'Unknown Campaign'}
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                {entity?.platform?.toUpperCase() || 'Unknown'} • {entity?.type || 'Campaign'}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <p style={{
                        margin: '0 0 var(--space-4) 0',
                        fontSize: 'var(--font-size-md)',
                        color: 'var(--color-text-primary)',
                        lineHeight: 1.5
                    }}>
                        {config.description}
                    </p>

                    {/* Current Metrics */}
                    {metrics && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 'var(--space-3)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <MetricBox
                                label="Current Spend"
                                value={formatCurrency(metrics.spend || 0)}
                            />
                            <MetricBox
                                label="ROAS"
                                value={`${(metrics.roas || 0).toFixed(1)}x`}
                                highlight={metrics.roas >= 2}
                                negative={metrics.roas < 1}
                            />
                            <MetricBox
                                label="Budget"
                                value={formatCurrency(metrics.currentBudget || metrics.dailyBudget || 0)}
                            />
                        </div>
                    )}

                    {/* Budget Input (for scale action) */}
                    {config.showBudgetInput && (
                        <div style={{
                            marginBottom: 'var(--space-4)',
                            padding: 'var(--space-4)',
                            background: 'var(--color-surface)',
                            borderRadius: 'var(--radius-lg)'
                        }}>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                color: 'var(--color-text-secondary)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                New Daily Budget
                            </label>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)'
                            }}>
                                <DollarSign size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                                <input
                                    type="number"
                                    value={budgetAmount}
                                    onChange={(e) => setBudgetAmount(parseFloat(e.target.value) || 0)}
                                    min="1"
                                    step="1"
                                    style={{
                                        flex: 1,
                                        padding: 'var(--space-3)',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: 'var(--radius-md)',
                                        fontSize: 'var(--font-size-lg)',
                                        fontWeight: 600,
                                        background: 'var(--color-bg-primary)',
                                        color: 'var(--color-text-primary)'
                                    }}
                                />
                            </div>
                            {metrics?.currentBudget && budgetAmount > metrics.currentBudget && (
                                <div style={{
                                    marginTop: 'var(--space-2)',
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-success)'
                                }}>
                                    +{Math.round(((budgetAmount - metrics.currentBudget) / metrics.currentBudget) * 100)}% increase
                                </div>
                            )}
                        </div>
                    )}

                    {/* Impact Preview */}
                    {impactAmount > 0 && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3) var(--space-4)',
                            background: impactType === 'save'
                                ? 'rgba(34, 197, 94, 0.1)'
                                : 'rgba(124, 58, 237, 0.1)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <DollarSign size={18} style={{
                                color: impactType === 'save'
                                    ? 'var(--color-success)'
                                    : 'var(--color-primary)'
                            }} />
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                color: impactType === 'save'
                                    ? 'var(--color-success)'
                                    : 'var(--color-primary)'
                            }}>
                                Estimated {impactType === 'save' ? 'savings' : 'gain'}: {formatCurrency(impactAmount)}
                            </span>
                        </div>
                    )}

                    {/* Warning */}
                    {config.warning && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3) var(--space-4)',
                            background: 'rgba(234, 179, 8, 0.1)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <AlertTriangle size={18} style={{
                                color: 'rgb(234, 179, 8)',
                                flexShrink: 0,
                                marginTop: '2px'
                            }} />
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.4
                            }}>
                                {config.warning}
                            </span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-4) var(--space-5)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-surface)'
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            flex: 1,
                            padding: 'var(--space-3) var(--space-4)',
                            background: 'var(--color-bg-primary)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-md)',
                            fontWeight: 500,
                            color: 'var(--color-text-secondary)',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all var(--transition-fast)'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        style={{
                            flex: 1,
                            padding: 'var(--space-3) var(--space-4)',
                            background: config.confirmColor,
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--font-size-md)',
                            fontWeight: 500,
                            color: 'white',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 'var(--space-2)',
                            opacity: loading ? 0.7 : 1,
                            transition: 'all var(--transition-fast)'
                        }}
                    >
                        {loading && <Loader2 size={18} className="spin" />}
                        {config.confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

const MetricBox = ({ label, value, highlight, negative }) => (
    <div style={{
        padding: 'var(--space-3)',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-md)',
        textAlign: 'center'
    }}>
        <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-tertiary)',
            marginBottom: '4px'
        }}>
            {label}
        </div>
        <div style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: highlight
                ? 'var(--color-success)'
                : negative
                    ? 'var(--color-error)'
                    : 'var(--color-text-primary)'
        }}>
            {value}
        </div>
    </div>
);

export default ActionConfirmationModal;
